import styled from "@emotion/styled";
import { Menu, Typography } from "@mindlercare/ui-web";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useAuth, useInternalAuth } from "../AuthenticationProvider";
import { useStateNavigator } from "../hooks/useStateNavigator";
import { useTranslation } from "../hooks/useTranslation";
import { TranslationKey } from "../translations";
import { AuthenticationView as TSignInOptions } from "../types";

const signInOptions: { id: TSignInOptions; label: string }[] = [
  {
    id: "qr-code",
    label: "WITH_BANKID_QR_CODE",
  },
  {
    id: "bank-id-this-device",
    label: "WITH_BANKID_SAME_DEVICE",
  },
  {
    id: "email",
    label: "WITH_EMAIL",
  },
  {
    id: "passkey",
    label: "WITH_PASSKEY",
  },
];

const signInOptionsSE: TSignInOptions[] = [
  "qr-code",
  "bank-id-this-device",
  "passkey",
];
const signInOptionsXG: TSignInOptions[] = ["email", "passkey"];

export const SignInOptions = () => {
  const { t } = useTranslation();
  const { market, canUseMagicLogin } = useInternalAuth();
  const { currentState, navigate, replace } = useStateNavigator();
  const { loginState } = useAuth();
  const magicLoginEnabled = canUseMagicLogin();
  const lastSignInOptionId = useSyncExternalStore(
    subscribe,
    getLastSignInOption,
  );

  const availableSignInOptions = useMemo(
    () => [
      ...signInOptions.filter((option) =>
        market === "SE"
          ? signInOptionsSE.includes(option.id)
          : signInOptionsXG.includes(option.id),
      ),
      ...(magicLoginEnabled
        ? [
            {
              id: "magic-login",
              label: "WITH_MAGIC_LOGIN",
            } as { id: TSignInOptions; label: string },
          ]
        : []),
    ],
    [market, magicLoginEnabled],
  );

  const lastSignInOptionIndex = availableSignInOptions.findIndex(
    (option) => option.id === lastSignInOptionId,
  );
  const [selectedOption, setSelectedOption] = useState<
    (typeof signInOptions)[number]
  >(signInOptions[lastSignInOptionIndex > -1 ? lastSignInOptionIndex : 0]);

  useEffect(() => {
    if (!currentState) {
      setSelectedOption(
        availableSignInOptions[
          lastSignInOptionIndex > -1 ? lastSignInOptionIndex : 0
        ],
      );
      replace({
        view: availableSignInOptions[
          lastSignInOptionIndex > -1 ? lastSignInOptionIndex : 0
        ].id,
        market,
      });
      return;
    }

    if (currentState.market !== market) {
      setSelectedOption(
        availableSignInOptions[
          lastSignInOptionIndex > -1 ? lastSignInOptionIndex : 0
        ],
      );
      navigate({
        view: availableSignInOptions[
          lastSignInOptionIndex > -1 ? lastSignInOptionIndex : 0
        ].id,
        market,
      });
      return;
    }

    setSelectedOption(
      availableSignInOptions.find(
        (option) => option.id === currentState.view,
      ) ??
        availableSignInOptions[
          lastSignInOptionIndex > -1 ? lastSignInOptionIndex : 0
        ],
    );
  }, [
    currentState,
    market,
    navigate,
    replace,
    availableSignInOptions,
    lastSignInOptionIndex,
  ]);

  return (
    <Menu>
      <Menu.Trigger
        variant="secondary"
        disabled={loginState === "login-in-progress"}
        withExpandIcon
      >
        <SignInMenuTrigger>
          <Typography variant="body-small">
            {t(selectedOption.label as TranslationKey)}
          </Typography>
        </SignInMenuTrigger>
      </Menu.Trigger>
      <Menu.Items>
        {availableSignInOptions.map((option) => (
          <Menu.Item
            key={option.id}
            onClick={() => {
              setSelectedOption(option);
              changeLastSignInOption(option.id as LastSignInOption);
              navigate({ view: option.id, market });
            }}
          >
            <Typography variant="body-small">
              {t(option.label as TranslationKey)}
            </Typography>
          </Menu.Item>
        ))}
      </Menu.Items>
    </Menu>
  );
};

const SignInMenuTrigger = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LAST_SIGN_IN_OPTION = "auth:last-sign-in-option";

export const getLastSignInOption = () => {
  return localStorage.getItem(LAST_SIGN_IN_OPTION);
};

type LastSignInOption = Extract<
  TSignInOptions,
  "email" | "passkey" | "qr-code" | "bank-id-this-device" | "magic-login"
>;

const changeLastSignInOption = (signInOption: LastSignInOption) => {
  localStorage.setItem(LAST_SIGN_IN_OPTION, signInOption);
  window.dispatchEvent(new Event(LAST_SIGN_IN_OPTION));
};

const subscribe = (callback: () => void) => {
  window.addEventListener(LAST_SIGN_IN_OPTION, callback);

  return () => {
    window.removeEventListener(LAST_SIGN_IN_OPTION, callback);
  };
};
